/**
 * Phase 8 · C-25 — Interview summary templates service.
 *
 * Replaces the `ggj.interview.summary.templates.v1.<orgId>` localStorage
 * bucket with a real org-scoped table (`ggj_b2b_interview_templates`,
 * migration 101). Read + mutate surface used by InterviewTemplateRail.
 */
import { supabase } from '../client';

export interface InterviewTemplate {
  id: string;
  orgId: string;
  name: string;
  /** AI prompt body. Nullable in the DB but we surface it as a string for the form. */
  body: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

interface InterviewTemplateRow {
  id: string;
  org_id: string;
  name: string;
  body: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

function mapRow(r: InterviewTemplateRow): InterviewTemplate {
  return {
    id: r.id,
    orgId: r.org_id,
    name: r.name,
    body: r.body ?? '',
    createdBy: r.created_by,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

export interface SaveInterviewTemplateInput {
  /** When provided we UPDATE that row; omit it (or pass null) to INSERT. */
  id?: string | null;
  name: string;
  body: string;
}

export const interviewTemplatesService = {
  /**
   * List all templates for an org, newest first. Throws on error so TanStack
   * Query can surface `isError` and the UI can show a retry state.
   */
  async list(orgId: string): Promise<InterviewTemplate[]> {
    const { data, error } = await supabase
      .from('ggj_b2b_interview_templates')
      .select('id, org_id, name, body, created_by, created_at, updated_at')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false });

    if (error) throw new Error(error.message);
    return (data ?? []).map((r) => mapRow(r as InterviewTemplateRow));
  },

  /**
   * Create when `input.id` is absent, update when it's set. `created_by` is
   * recorded from the authenticated Supabase user when available; RLS
   * guarantees tenancy even if the client lies about org_id.
   */
  async save(orgId: string, input: SaveInterviewTemplateInput): Promise<InterviewTemplate> {
    const name = input.name.trim();
    const body = input.body.trim();
    if (!name) throw new Error('Template name is required.');

    if (input.id) {
      const { data, error } = await supabase
        .from('ggj_b2b_interview_templates')
        .update({ name, body })
        .eq('id', input.id)
        .eq('org_id', orgId)
        .select('id, org_id, name, body, created_by, created_at, updated_at')
        .single();
      if (error) throw new Error(error.message);
      return mapRow(data as InterviewTemplateRow);
    }

    const authResp = await supabase.auth.getUser();
    const createdBy = authResp.data.user?.id ?? null;

    const { data, error } = await supabase
      .from('ggj_b2b_interview_templates')
      .insert({ org_id: orgId, name, body, created_by: createdBy })
      .select('id, org_id, name, body, created_by, created_at, updated_at')
      .single();
    if (error) throw new Error(error.message);
    return mapRow(data as InterviewTemplateRow);
  },

  async delete(orgId: string, id: string): Promise<void> {
    const { error } = await supabase
      .from('ggj_b2b_interview_templates')
      .delete()
      .eq('id', id)
      .eq('org_id', orgId);
    if (error) throw new Error(error.message);
  },
};
